import { useAppStore } from "./store";

export type Lang = "en" | "bn";

export const translations = {
  common: {
    calculate: { en: "Calculate", bn: "হিসাব করুন" },
    reset: { en: "Reset", bn: "রিসেট" },
    result: { en: "Result", bn: "ফলাফল" },
    copy: { en: "Copy", bn: "কপি" },
    copied: { en: "Copied to clipboard", bn: "ক্লিপবোর্ডে কপি হয়েছে" },
    share: { en: "Share", bn: "শেয়ার" },
    home: { en: "Home", bn: "হোম" },
    categories: { en: "Categories", bn: "ক্যাটাগরি" },
    related: { en: "Related Calculators", bn: "সম্পর্কিত ক্যালকুলেটর" },
    faq: { en: "Frequently Asked Questions", bn: "প্রায়শই জিজ্ঞাসিত প্রশ্ন" },
    allCalculators: { en: "All Calculators", bn: "সকল ক্যালকুলেটর" },
    popular: { en: "Popular", bn: "জনপ্রিয়" },
    searchPlaceholder: { en: "Search calculators...", bn: "ক্যালকুলেটর খুঁজুন..." },
    comingSoon: { en: "Coming soon", bn: "শীঘ্রই আসছে" },
    placeholderBody: {
      en: "This calculator's interactive UI is on the way. The page, routing, SEO meta tags and bilingual layout are wired up — inputs and formulas land next.",
      bn: "এই ক্যালকুলেটরের ইন্টারঅ্যাক্টিভ ইন্টারফেস শীঘ্রই আসছে। পেজ, রাউটিং, SEO ও দ্বিভাষিক লেআউট প্রস্তুত — ইনপুট ও সূত্র পরবর্তী ধাপে যোগ হবে।",
    },
    noResults: {
      en: "No calculators match your search.",
      bn: "আপনার খোঁজে কোনো ক্যালকুলেটর পাওয়া যায়নি।",
    },
    featured: { en: "Featured", bn: "বিশেষ নির্বাচন" },
    bdExclusive: { en: "BD Exclusive", bn: "বাংলাদেশ এক্সক্লুসিভ" },
    whyTitle: { en: "Why Bangla Calculator?", bn: "কেন বাংলা ক্যালকুলেটর?" },
    whyBd: { en: "Built for Bangladesh", bn: "বাংলাদেশের জন্য তৈরি" },
    whyBdDesc: {
      en: "Local context: BDT, taxes, VAT, land units (katha, bigha) — done right.",
      bn: "স্থানীয় প্রেক্ষাপট: টাকা, কর, ভ্যাট, জমির একক (কাঠা, বিঘা) — একদম সঠিক।",
    },
    whyBilingual: { en: "Fully Bilingual", bn: "সম্পূর্ণ দ্বিভাষিক" },
    whyBilingualDesc: {
      en: "Switch between English and বাংলা anytime — including numbers.",
      bn: "যেকোনো সময় ইংরেজি ও বাংলার মধ্যে পরিবর্তন — সংখ্যা সহ।",
    },
    whyFree: { en: "Free, No Signup", bn: "বিনামূল্যে, সাইন-আপ নেই" },
    whyFreeDesc: {
      en: "Every calculator is free. No accounts, no email, no tracking pixels.",
      bn: "প্রতিটি ক্যালকুলেটর বিনামূল্যে। অ্যাকাউন্ট, ইমেইল বা ট্র্যাকিং নেই।",
    },
    whyFast: { en: "Lightning Fast", bn: "অত্যন্ত দ্রুত" },
    whyFastDesc: {
      en: "Runs entirely in your browser — instant answers, works offline.",
      bn: "সম্পূর্ণ ব্রাউজারে চলে — তাৎক্ষণিক ফলাফল, অফলাইনেও কাজ করে।",
    },
  },
  brand: {
    appName: { en: "Bangla Calculator", bn: "বাংলা ক্যালকুলেটর" },
    tagline: {
      en: "Every calculator, built for Bangladesh",
      bn: "বাংলাদেশের জন্য তৈরি সকল ক্যালকুলেটর",
    },
  },
  footer: {
    about: { en: "About", bn: "আমাদের সম্পর্কে" },
    privacy: { en: "Privacy", bn: "গোপনীয়তা" },
    terms: { en: "Terms", bn: "শর্তাবলী" },
    contact: { en: "Contact", bn: "যোগাযোগ" },
    copyright: {
      en: "© 2026 Bangla Calculator. All rights reserved.",
      bn: "© ২০২৬ বাংলা ক্যালকুলেটর। সর্বস্বত্ব সংরক্ষিত।",
    },
  },
  categories: {
    finance: { en: "Finance & Money", bn: "অর্থ ও আর্থিক" },
    health: { en: "Health & Fitness", bn: "স্বাস্থ্য ও ফিটনেস" },
    date: { en: "Date, Age & Time", bn: "তারিখ, বয়স ও সময়" },
    math: { en: "Math & Scientific", bn: "গণিত ও বৈজ্ঞানিক" },
    units: { en: "Unit Converters", bn: "একক রূপান্তর" },
    bd: { en: "Bangladesh Specific", bn: "বাংলাদেশ বিশেষ" },
  },
  // per-calculator strings added later
} as const;

export type Translations = typeof translations;
export type Namespace = keyof Translations;

type Entry = { en: string; bn: string };
type NSKeys<N extends Namespace> = keyof Translations[N];

/**
 * Dotted-path translator, e.g. t("common.calculate") or t("categories.finance").
 */
export function t<N extends Namespace, K extends NSKeys<N>>(
  path: `${N}.${K & string}`,
  lang: Lang,
): string {
  const [ns, key] = path.split(".") as [N, string];
  const entry = (translations[ns] as Record<string, Entry>)[key];
  return entry ? entry[lang] : path;
}

export function useT() {
  const lang = useAppStore((s) => s.lang);
  return {
    lang,
    t: <N extends Namespace, K extends NSKeys<N>>(path: `${N}.${K & string}`) =>
      t(path, lang),
  };
}
